import React, { useState, useEffect, useCallback } from 'react'
import { Form, Pagination, Card, Row, Col, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowClockwise,
  BsCalendar3,
  BsCheckCircleFill,
  BsXCircleFill,
  BsCheckLg,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const ALLOWED_STATUSES = ['present', 'absent', 'leave', 'half_day', 'late']

const mapTeacherToRow = (teacher, existingAtt = null) => ({
  id: teacher.id,
  name: teacher.name,
  email: teacher.email || '-',
  department: teacher.qualification || teacher.department || '-',
  status: existingAtt?.status || '',
  remarks: existingAtt?.remarks || '',
  attendance_id: existingAtt?.id ?? null,
})

const getSessionName = () =>
  Cookies.get('session_name') || Cookies.get('session') || ''

const getHolidayListFromResponse = (responseData) => {
  if (Array.isArray(responseData?.data)) return responseData.data
  if (responseData?.data && typeof responseData.data === 'object') {
    return [responseData.data]
  }
  if (Array.isArray(responseData?.holidays)) return responseData.holidays
  if (Array.isArray(responseData?.holiday)) return responseData.holiday
  if (responseData?.holiday && typeof responseData.holiday === 'object') {
    return [responseData.holiday]
  }
  return []
}

const TakeTeacherAttendance = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const institute_id = Cookies.get('institute_id')
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [openTable, setOpenTable] = useState(false)
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [allTeachers, setAllTeachers] = useState([])
  const [data, setData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedTeacherId, setSelectedTeacherId] = useState(null)
  const [loadingTeachers, setLoadingTeachers] = useState(false)
  const [loadingAttendance, setLoadingAttendance] = useState(false)
  const [checkingHoliday, setCheckingHoliday] = useState(false)
  const [tableMessage, setTableMessage] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchTeachers = useCallback(async () => {
    if (!institute_id) {
      toast.error('Institute not selected. Please log in again.')
      return []
    }
    setLoadingTeachers(true)
    try {
      const res = await ApiEndpoints.getAllTeachers({
        'institute-Id': Number(institute_id),
      })
      const teachers = (res.data?.data || []).filter(
        (t) => String(t.status || '').toLowerCase() === 'active',
      )
      setAllTeachers(teachers)
      return teachers
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to load teachers.',
      )
      setAllTeachers([])
      return []
    } finally {
      setLoadingTeachers(false)
    }
  }, [institute_id])

  const mergeTeachersWithAttendance = useCallback((teachers, attendanceList) => {
    const byTeacherId = {}
    ;(attendanceList || []).forEach((att) => {
      byTeacherId[Number(att.teacher_id)] = att
    })
    return teachers.map((teacher) =>
      mapTeacherToRow(teacher, byTeacherId[Number(teacher.id)]),
    )
  }, [])

  const isHolidayForTeacherAttendance = useCallback(async (date) => {
    setTableMessage('')
    const session = getSessionName()
    if (!session) {
      setTableMessage('Session not found. Please select institute again.')
      return true
    }

    setCheckingHoliday(true)
    try {
      const response = await ApiEndpoints.getHolidayByDate(session, { date })
      const holidays = getHolidayListFromResponse(response.data)
      const applicableHoliday = holidays.find((holiday) => {
        const holidayType = String(
          holiday.holiday_type || holiday.type || '',
        ).toUpperCase()
        return !holidayType || holidayType === 'ALL' || holidayType === 'STAFF'
      })

      if (applicableHoliday) {
        setTableMessage(
          applicableHoliday.title
            ? `Institue is on holiday in selected date: ${applicableHoliday.title}.`
            : 'Institue is on holiday in selected date.',
        )
        return true
      }

      return false
    } catch (error) {
      setTableMessage(
        error.response?.data?.message || 'Failed to check holiday for date.',
      )
      return true
    } finally {
      setCheckingHoliday(false)
    }
  }, [])

  const loadAttendanceForDate = useCallback(
    async (teachers, date, teacherIdFilter = null) => {
      if (!institute_id || !teachers.length) {
        setData([])
        setOpenTable(false)
        return
      }

      const isHoliday = await isHolidayForTeacherAttendance(date)
      if (isHoliday) {
        setData([])
        setOpenTable(true)
        return
      }

      setLoadingAttendance(true)
      try {
        const payload = { date }
        if (teacherIdFilter) {
          payload.teacher_id = Number(teacherIdFilter)
        }
        const res = await ApiEndpoints.getTeacherAttendance(payload)
        const attendanceList = res.data?.attendance || []
        let merged = mergeTeachersWithAttendance(teachers, attendanceList)
        if (teacherIdFilter) {
          merged = merged.filter((t) => t.id === Number(teacherIdFilter))
        }
        setTableMessage('')
        setData(merged)
        setOpenTable(true)
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            'Failed to load attendance for selected date.',
        )
        let merged = teachers.map((t) => mapTeacherToRow(t))
        if (teacherIdFilter) {
          merged = merged.filter((t) => t.id === Number(teacherIdFilter))
        }
        setData(merged)
        setOpenTable(merged.length > 0)
      } finally {
        setLoadingAttendance(false)
      }
    },
    [institute_id, isHolidayForTeacherAttendance, mergeTeachersWithAttendance],
  )

  useEffect(() => {
    fetchTeachers()
  }, [fetchTeachers])

  useEffect(() => {
    if (!allTeachers.length) return
    loadAttendanceForDate(allTeachers, selectedDate, selectedTeacherId)
  }, [selectedDate, selectedTeacherId, allTeachers, loadAttendanceForDate])

  const handleStatusChange = (teacherId, newStatus) => {
    setData((prev) =>
      prev.map((teacher) =>
        teacher.id === teacherId ? { ...teacher, status: newStatus } : teacher,
      ),
    )
  }

  const handleRemarksChange = (teacherId, newRemarks) => {
    setData((prev) =>
      prev.map((teacher) =>
        teacher.id === teacherId
          ? { ...teacher, remarks: newRemarks }
          : teacher,
      ),
    )
  }

  const handleTeacherChange = (teacherId) => {
    setSelectedTeacherId(teacherId || null)
    setCurrentPage(1)
  }

  const handleRefresh = async () => {
    setSelectedTeacherId(null)
    setCurrentPage(1)
    const teachers = await fetchTeachers()
    if (teachers.length) {
      await loadAttendanceForDate(teachers, selectedDate, null)
    } else {
      setData([])
      setOpenTable(false)
    }
  }

  const handleSaveAttendance = async () => {
    const isHoliday = await isHolidayForTeacherAttendance(selectedDate)
    if (isHoliday) {
      setData([])
      setOpenTable(true)
      return
    }

    const rowsWithStatus = data.filter((t) =>
      ALLOWED_STATUSES.includes(t.status),
    )

    if (rowsWithStatus.length === 0) {
      toast.error('Please select status for at least one teacher.')
      return
    }

    const toUpdate = rowsWithStatus.filter((t) => t.attendance_id)
    const toCreate = rowsWithStatus.filter((t) => !t.attendance_id)

    setSaving(true)
    try {
      const updatePromises = toUpdate.map((row) =>
        ApiEndpoints.updateTeacherAttendance(row.attendance_id, {
          status: row.status,
          remarks: row.remarks || undefined,
        }),
      )

      if (updatePromises.length) {
        await Promise.all(updatePromises)
      }

      if (toCreate.length) {
        const payload = {
          date: selectedDate,
          attendance: toCreate.map((row) => ({
            teacher_id: row.id,
            status: row.status,
            remarks: row.remarks || undefined,
          })),
        }
        await ApiEndpoints.markTeacherAttendance(payload)
      }

      toast.success(
        toUpdate.length && toCreate.length
          ? 'Teacher attendance saved and updated successfully.'
          : toUpdate.length
            ? 'Teacher attendance updated successfully.'
            : 'Teacher attendance recorded successfully.',
      )

      await loadAttendanceForDate(
        allTeachers,
        selectedDate,
        selectedTeacherId,
      )
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.attendance?.[0] ||
        'Failed to save teacher attendance.'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  )
  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage))
  const isLoading = loadingTeachers || loadingAttendance || checkingHoliday

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Take Teacher Attendance
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          <div
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Select Teacher
                        </h5>
                        <p
                          className="m-0 mt-1"
                          style={{ fontSize: '13px', color: '#6b7280' }}
                        >
                          Choose a teacher to take attendance (optional)
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          opacity: isLoading ? 0.6 : 1,
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body
                  className="p-4"
                  style={{
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >
                  <Row className="g-3 text-start">
                    <Col xs={12} md={6} lg={4}>
                      <div style={{ position: 'relative' }}>
                        <label
                          className="fw-semibold mb-2 d-block"
                          style={{
                            color: '#6b7280',
                            fontSize: '13px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Select Teacher
                        </label>
                        <Typeahead
                          id="teacher-select"
                          labelKey={(option) =>
                            `${option.name}`
                          }
                          onChange={(selected) => {
                            const teacher = selected[0]
                            handleTeacherChange(teacher ? teacher.id : '')
                          }}
                          options={allTeachers}
                          placeholder={
                            loadingTeachers
                              ? 'Loading teachers...'
                              : 'Select Teacher (optional)'
                          }
                          disabled={loadingTeachers}
                          selected={allTeachers.filter(
                            (t) => t.id === Number(selectedTeacherId || ''),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            },
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {openTable && (
            <div
              className={`mb-4 ${
                isMobile
                  ? 'd-flex flex-column gap-3'
                  : 'd-flex justify-content-between align-items-center'
              }`}
            >
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div
                  className="d-flex align-items-center"
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    gap: '8px',
                    border: '1px solid #D1D3D4',
                  }}
                >
                  <span
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Show:
                  </span>
                  <Form.Select
                    className="w-auto"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value, 10))
                      setCurrentPage(1)
                    }}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #D1D3D4',
                      backgroundColor: '#ffffff',
                      color: '#111827',
                      fontSize: '13px',
                      fontWeight: '500',
                      padding: '4px 32px 4px 12px',
                      minWidth: '140px',
                      height: '36px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    <option value="10">10 per page</option>
                    <option value="20">20 per page</option>
                    <option value="30">30 per page</option>
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div style={{ position: 'relative' }}>
                    <BsCalendar3
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6b7280',
                        fontSize: '16px',
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      disabled={loadingAttendance || checkingHoliday}
                      style={{
                        paddingLeft: '40px',
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4',
                        fontSize: '14px',
                        height: '40px',
                        backgroundColor: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        width: '180px',
                      }}
                    />
                  </div>
                </div>
              </div>
              {data.length > 0 && !isLoading && (
                <div
                  className={`d-flex gap-3 flex-wrap ${
                    isMobile ? 'w-100' : ''
                  }`}
                >
                  <div className="d-flex align-items-center gap-2">
                    <button
                      onClick={() => {
                        setData((prev) =>
                          prev.map((teacher) => ({
                            ...teacher,
                            status: 'present',
                          })),
                        )
                      }}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        border: '1px solid #10B981',
                        backgroundColor: '#10B981',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      title="Mark All Present"
                    >
                      <BsCheckCircleFill size={20} />
                    </button>
                    {!isMobile && (
                      <span
                        style={{
                          fontSize: '14px',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: '500',
                        }}
                      >
                        Mark All Present
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      onClick={() => {
                        setData((prev) =>
                          prev.map((teacher) => ({
                            ...teacher,
                            status: 'absent',
                          })),
                        )
                      }}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        border: '1px solid #EF4444',
                        backgroundColor: '#EF4444',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      title="Mark All Absent"
                    >
                      <BsXCircleFill size={20} />
                    </button>
                    {!isMobile && (
                      <span
                        style={{
                          fontSize: '14px',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: '500',
                        }}
                      >
                        Mark All Absent
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
              <p
                className="mt-3"
                style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Loading teachers and attendance...
              </p>
            </div>
          )}

          {!isLoading && openTable && data.length > 0 ? (
            <>
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                }}
              >
                <Table
                  className="table mb-0"
                  style={{ borderRadius: '12px', overflow: 'hidden' }}
                >
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Teacher Name
                      </Th> <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Email
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Qualification
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Status
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Remarks
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedData.map((item, index) => (
                      <Tr
                        key={item.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0
                              ? '#ffffff'
                              : 'rgba(243, 244, 246, 0.4)',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <Td
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: 'none',
                            color: '#111827',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {item.name}
                          {item.attendance_id && (
                            <span
                              className="ms-2 badge bg-secondary"
                              style={{ fontSize: '10px' }}
                            >
                              Marked
                            </span>
                          )}
                        </Td>
                        <Td
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: 'none',
                            color: '#111827',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {item.email}
                        </Td>
                        <Td
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: 'none',
                            color: '#111827',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {item.department}
                        </Td>
                        <Td
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: 'none',
                            color: '#111827',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          <Form.Select
                            value={item.status || ''}
                            onChange={(e) =>
                              handleStatusChange(item.id, e.target.value)
                            }
                            disabled={saving}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            <option value="">Select</option>
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                            <option value="leave">Leave</option>
                            <option value="half_day">Half Day</option>
                            <option value="late">Late</option>
                          </Form.Select>
                        </Td>
                        <Td
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: 'none',
                            color: '#111827',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          <Form.Control
                            type="text"
                            placeholder="Enter remarks"
                            value={item.remarks || ''}
                            onChange={(e) =>
                              handleRemarksChange(item.id, e.target.value)
                            }
                            disabled={
                              saving ||
                              (item.status !== 'present' &&
                                item.status !== 'leave')
                            }
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Card>

              <div className="d-flex flex-wrap justify-content-between align-items-center mb-5 mt-4 gap-2">
                <div className="d-flex align-items-center gap-2">
                  <button
                    onClick={handleSaveAttendance}
                    disabled={saving || checkingHoliday}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      border: '1px solid #10B981',
                      backgroundColor:
                        saving || checkingHoliday ? '#9CA3AF' : '#10B981',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor:
                        saving || checkingHoliday ? 'not-allowed' : 'pointer',
                    }}
                    title="Save Attendance"
                  >
                    {saving || checkingHoliday ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <BsCheckLg size={20} />
                    )}
                  </button>
                  {!isMobile && (
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: '500',
                      }}
                    >
                      {saving
                        ? 'Saving...'
                        : checkingHoliday
                          ? 'Checking Holiday...'
                          : 'Save Attendance'}
                    </span>
                  )}
                </div>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  />
                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage >= totalPages}
                  />
                </Pagination>
              </div>
            </>
          ) : !isLoading && openTable ? (
            <Card
              style={{
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
              }}
            >
              <Card.Body>
                <div className="text-center py-5">
                  <p
                    style={{
                      color: tableMessage ? '#B91C1C' : '#6b7280',
                      fontSize: '14px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontWeight: tableMessage ? '600' : '400',
                    }}
                  >
                    {tableMessage || 'No teachers found.'}
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : !isLoading && !openTable ? (
            <Card
              style={{
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
              }}
            >
              <Card.Body>
                <div className="text-center py-5">
                  <p
                    style={{
                      color: '#6b7280',
                      fontSize: '14px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    No active teachers available for this institute.
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default TakeTeacherAttendance
